// Frontend wrappers for the contactGroups (labels) backend endpoints + a
// Firestore reader for the cached app-wide labels collection. The backend
// endpoints are spec'd at docs/superpowers/specs/contacts-backend-snippet.js
// and don't exist yet — until Humza ships them, every network call here
// will fail. Callers should toast a soft "Sync not available yet" message
// and not break.

import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";

const API_BASE =
  "https://us-central1-team-edelstein.cloudfunctions.net/api";

// --------------------------------------------------------------------------
// Firestore reads (cached labels)
// --------------------------------------------------------------------------

// Load all contactGroups for a given user from Firestore. Cheap — these are
// upserted on every /list-contact-groups call by the backend.
export async function loadContactGroupsFromFirestore(userId) {
  try {
    const q = userId
      ? query(
          collection(firestored, "contactGroups"),
          where("ownerId", "==", userId),
        )
      : collection(firestored, "contactGroups");
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      );
  } catch (err) {
    console.error("loadContactGroupsFromFirestore failed:", err);
    return [];
  }
}

// --------------------------------------------------------------------------
// Backend endpoint wrappers
// --------------------------------------------------------------------------

// Pull labels from Google, upsert to Firestore, return the list.
// Throws if endpoint is not yet deployed.
export async function syncContactGroupsFromGoogle(userId) {
  const res = await fetch(
    `${API_BASE}/list-contact-groups?userId=${encodeURIComponent(userId)}`,
    { method: "GET" },
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.groups || [];
}

export async function createContactGroup(userId, name) {
  const res = await fetch(`${API_BASE}/create-contact-group`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId, name }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function renameContactGroup(userId, resourceName, name) {
  const res = await fetch(`${API_BASE}/update-contact-group`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId, resourceName, name }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function deleteContactGroup(
  userId,
  resourceName,
  deleteContacts = false,
) {
  const res = await fetch(`${API_BASE}/delete-contact-group`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId, resourceName, deleteContacts }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// Update a contact's label memberships. add / remove arrays of group
// resourceNames; returns the new labels array.
export async function updateContactMemberships({
  userId,
  resourceName,
  contactId,
  addGroupResourceNames = [],
  removeGroupResourceNames = [],
}) {
  const res = await fetch(`${API_BASE}/update-contact-memberships`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      userId,
      resourceName,
      contactId,
      addGroupResourceNames,
      removeGroupResourceNames,
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// Helper: is the backend ready? Hits /list-contact-groups and returns true
// if it responds OK. Frontend uses this to decide whether to show real
// label UI or a "coming soon" placeholder.
export async function isLabelsBackendLive(userId) {
  try {
    const res = await fetch(
      `${API_BASE}/list-contact-groups?userId=${encodeURIComponent(userId)}`,
      { method: "GET" },
    );
    return res.status !== 404;
  } catch {
    return false;
  }
}
